import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaInfoCircle, FaChevronDown, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import { isAlaCarteItemHiddenFromPurchase } from '../utils/programPricing';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalYourPlanBanner from '../components/PortalYourPlanBanner';
import { usePortalLinkedAccount } from '../hooks/usePortalLinkedAccount';
import { CUSTOM_PROGRAM, CUSTOM_TIER } from './ProgramDetail';
import { isRetiredProgram } from '../utils/retiredPrograms';

// `embedded` drops the page shell (background, back button, plan banner) so the
// locked dashboard can host the program cards over its own PortalBackground.
const PortalPrograms = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const { account: linkedAccount } = usePortalLinkedAccount();
  const [selectedTier, setSelectedTier] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState({});
  const [programs, setPrograms] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [itemOrder, setItemOrder] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [collapsedDepartments, setCollapsedDepartments] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedShowcaseFeature, setSelectedShowcaseFeature] = useState(null);

  // Load data from Firestore on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load programs data
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach(doc => {
            const programData = doc.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              if (!Array.isArray(programData.showcaseFeatures)) {
                programData.showcaseFeatures = Object.entries(programData.features || {})
                  .filter(([_, fd]) => fd?.showcase)
                  .map(([name]) => ({ text: name, showcase: true, description: '' }));
              } else {
                programData.showcaseFeatures = (programData.showcaseFeatures || []).map(item =>
                  typeof item === 'string' ? { text: item, showcase: true, description: '' } : { ...item, description: item.description || '' }
                );
              }
              if (!programData.name) programData.name = doc.id;
              if (!programData.description) programData.description = 'Program description';
              if (!programData.pricePerMonth) programData.pricePerMonth = 0;
              if (typeof programData.isMostPopular !== 'boolean') programData.isMostPopular = false;
              
              programsData[doc.id] = programData;
            }
          });
          setPrograms(programsData);
          
          // Set first program as selected if none selected
          if (!selectedTier || !programsData[selectedTier]) {
            setSelectedTier(Object.keys(programsData)[0] || '');
          }
        }

        // Load billing cycles data
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach(doc => {
            billingData[doc.id] = doc.data();
          });
          setBillingCycles(billingData);
          
          // Set first billing cycle as default
          if (Object.keys(billingData).length > 0) {
            setBillingCycle(Object.keys(billingData)[0]);
          }
        }

        // Load available items from alaCarteItems collection
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = itemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableItems(itemsData);

        // Load item order
        await loadItemOrder();
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Load item order from Firestore
  const loadItemOrder = async () => {
    try {
      const orderDoc = await getDocs(collection(db, 'settings'));
      const orderData = orderDoc.docs.find(doc => doc.id === 'itemOrder');
      if (orderData) {
        setItemOrder(orderData.data().order || {});
      }
    } catch (error) {
      console.error('Error loading item order:', error);
    }
  };

  // Get showcase features for a program (freeform text rows with description)
  const getShowcaseFeatures = (programKey) => {
    const program = programs[programKey];
    if (!program || !program.showcaseFeatures) return [];
    return (program.showcaseFeatures || [])
      .map(item => typeof item === 'string' ? { text: item, showcase: true, description: '' } : { ...item, description: item.description || '' })
      .filter(item => item.showcase && item.text?.trim());
  };

  // Get ordered items for a department
  const getOrderedItems = (department) => {
    // Unpromoted items aren't for sale, so they stay out of the comparison table.
    const deptItems = availableItems.filter(
      item => item.department === department && !isAlaCarteItemHiddenFromPurchase(item)
    );
    const savedOrder = itemOrder[department] || [];
    
    if (savedOrder.length === 0 && deptItems.length > 0) {
      return deptItems;
    }
    
    // Sort items according to saved order
    const orderedItems = [];
    savedOrder.forEach(orderedItem => {
      const item = deptItems.find(deptItem => deptItem.id === orderedItem.id);
      if (item) {
        orderedItems.push(item);
      }
    });
    
    // Add any new items that weren't in the saved order
    deptItems.forEach(item => {
      if (!orderedItems.find(orderedItem => orderedItem.id === item.id)) {
        orderedItems.push(item);
      }
    });
    
    return orderedItems;
  };

  // Toggle department collapse state
  const toggleDepartment = (department) => {
    setCollapsedDepartments(prev => ({
      ...prev,
      [department]: !prev[department]
    }));
  };

  // Show item details modal
  const showItemDetails = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  // Close item details modal
  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  // Show showcase feature details modal
  const showShowcaseFeatureDetails = (feature) => {
    setSelectedShowcaseFeature(feature);
  };

  const closeShowcaseFeatureModal = () => {
    setSelectedShowcaseFeature(null);
  };

  // Save program selection data to user document and account
  const saveProgramSelection = async (programData, programKey, billingCycle) => {
    try {
      if (!currentUser) {
        console.error('No current user found');
        return;
      }

      console.log('Saving program selection for user:', currentUser.email);
      console.log('Program data:', programData);
      console.log('Program key:', programKey);
      console.log('Billing cycle:', billingCycle);

      // Capitalize billing cycle for proper display
      const capitalizedBillingCycle = billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1);
      
      // Save to user's document
      const userData = {
        selectedProgram: programData.name,
        selectedProgramKey: programKey,
        billingCycle: capitalizedBillingCycle,
        programSelectionData: {
          program: programData,
          programKey: programKey,
          billingCycle: capitalizedBillingCycle,
          selectedAt: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', currentUser.uid), userData);
      console.log('Program selection saved to user document');

      // Try to find and update the account
      console.log('Attempting to save program selection to account for user:', currentUser.email);
      
      // Find the account associated with this user
      let accountsSnapshot = await getMyAccountSnapshot();
      
      if (!accountsSnapshot.empty) {
        const account = accountsSnapshot.docs[0];
        const accountRef = doc(db, 'accounts', account.id);
        
        console.log('Updating account with program selection data');
        
        await updateDoc(accountRef, {
          selectedProgram: programData.name,
          selectedProgramKey: programKey,
          billingCycle: capitalizedBillingCycle,
          programSelectionData: {
            program: programData,
            programKey: programKey,
            billingCycle: capitalizedBillingCycle,
            selectedAt: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        });
        
        console.log('Program selection saved to account');
        return account.id;
      } else {
        console.log('No account found for user:', currentUser.email);
        return null;
      }
      
    } catch (error) {
      console.error('Error saving program selection:', error);
      return null;
    }
  };

  // Validate program data to ensure it has required structure
  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') {
      return {
        name: 'Unknown Program',
        description: 'Program description',
        pricePerMonth: 0,
        isMostPopular: false,
        minimumTerm: 12,
        features: {}
      };
    }
    
    return {
      name: program.name || 'Unknown Program',
      description: program.description || 'Program description',
      pricePerMonth: program.pricePerMonth || 0,
      isMostPopular: program.isMostPopular || false,
      minimumTerm: program.minimumTerm || 12,
      features: program.features && typeof program.features === 'object' ? program.features : {},
      showcaseFeatures: Array.isArray(program.showcaseFeatures) ? program.showcaseFeatures : []
    };
  };

  const getAnnualPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * billingCycles.annual.months * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 12;
  };

  const getMonthlyPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth;
  };

  const getQuarterlyPrice = (pricePerMonth) => {
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 3;
  };

  // Get program color based on name or default
  const getProgramColor = (programName) => {
    const colors = {
      'Business Launch Blueprint': 'from-blue-500 to-blue-600',
      'Operational Excellence': 'from-green-500 to-green-600',
      'Financial Mastery': 'from-purple-500 to-purple-600',
      'Sales & Marketing Excellence': 'from-pink-500 to-pink-600',
      'Technology & Innovation': 'from-indigo-500 to-indigo-600'
    };
    return colors[programName] || 'from-red-500 to-red-600';
  };

  // Get department color for styling
  const getDepartmentColor = (department) => {
    const colors = {
      'General': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'Sales': 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200',
      'Operations': 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200',
      'Finance': 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-200',
      'Technology': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-700 dark:text-indigo-200',
      'Marketing': 'bg-pink-100 text-pink-800 dark:bg-pink-700 dark:text-pink-200',
      'HR': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-200'
    };
    return colors[department] || colors['General'];
  };

  // Same order as Program Templates: others first (object order), then Pro, Premier, BBA Bundle
  const getOrderedPrograms = () => {
    if (!programs) return [];
    const tailOrder = ['Pro', 'Premier', 'BBA Bundle'];
    const orderIndex = (key) => {
      const i = tailOrder.indexOf(key);
      return i >= 0 ? i + 1000 : 0;
    };
    return Object.entries(programs)
      .filter(([key, program]) => !isRetiredProgram(key, program))
      .sort(([keyA], [keyB]) => orderIndex(keyA) - orderIndex(keyB));
  };
  const hasStandardPrograms = getOrderedPrograms().length > 0;

  // Get ordered program keys
  const getOrderedProgramKeys = () => {
    return getOrderedPrograms().map(([key]) => key);
  };

  return (
    <div className={embedded ? '' : `min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`max-w-7xl mx-auto ${embedded ? '' : 'px-4 sm:px-6 lg:px-8 py-8'}`}>
        {/* Header Row */}
        {embedded ? (
          <div className="mb-6 text-center">
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Choose Your Ideal Growth Plan
            </h1>
          </div>
        ) : (
          <div className="mb-6 flex items-center justify-between">
            <BackButton to="/portal/billing" />
            <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Choose Your Ideal Growth Plan
            </h1>
            <div className="w-20"></div> {/* Spacer to balance the layout */}
          </div>
        )}

        {!embedded && linkedAccount?.program && <PortalYourPlanBanner account={linkedAccount} />}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className={`inline-flex items-center px-4 py-2 text-sm font-medium ${darkMode ? 'text-gray-300 bg-gray-800 border-gray-600' : 'text-gray-700 bg-white border-gray-300'} border rounded-md`}>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading programs and billing cycles...
            </div>
          </div>
        )}

        {/* Content - Only show when not loading */}
        {!isLoading && (
          <>
            {/* Billing Toggle */}
            <div className="flex justify-center mb-8">
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-1 shadow-sm border`}>
                <div className="flex">
                  {['monthly', 'quarterly', 'annual'].map((cycleKey) => {
                    const cycle = billingCycles[cycleKey];
                    if (!cycle) return null;
                    
                    return (
                      <button
                        key={cycleKey}
                        onClick={() => setBillingCycle(cycleKey)}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                          billingCycle === cycleKey
                            ? 'bg-red-600 text-white'
                            : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {cycle.name}
                        {cycle.discount > 0 && (
                          <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                            Save {cycle.discount}%
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {hasStandardPrograms && (
              <>
            {/* Program Cards - pt-10 so "Most Popular" pill above card isn't clipped */}
            <div className="grid grid-cols-3 gap-8 mb-12 pt-10 overflow-visible">
              {programs && getOrderedPrograms().map(([programKey, program], index) => {
                const validatedProgram = validateProgram(program);
                const keyLower = programKey.toLowerCase();
                const nameLower = (validatedProgram.name || '').toLowerCase();
                const isPlusProgram = keyLower.includes('plus') || nameLower.includes('plus');
                const isMostPopular = validatedProgram.isMostPopular;
                return (
                  <div
                    key={programKey}
                    className={`relative overflow-visible ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl h-full flex flex-col ${
                      isMostPopular
                        ? 'ring-2 ring-red-500 ring-offset-2 border-red-500'
                        : darkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {validatedProgram.isMostPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                        <span className="bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    <div className="p-8 flex flex-col h-full">
                      <div className="text-center mb-6">
                        <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>{validatedProgram.name}</h3>
                        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{validatedProgram.description}</p>
                      </div>

                      <div className="text-center mb-6">
                        {billingCycle === 'monthly' && (
                          <div className="flex items-baseline justify-center">
                            <span className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>$</span>
                            <span className={`text-5xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} ml-1`}>
                              {validatedProgram.pricePerMonth}
                            </span>
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} ml-2`}>/month</span>
                          </div>
                        )}
                        {billingCycle === 'quarterly' && billingCycles?.quarterly && (
                          <>
                            <div className="flex items-baseline justify-center">
                              <span className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>$</span>
                              <span className={`text-5xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} ml-1`}>
                                {getQuarterlyPrice(validatedProgram.pricePerMonth)}
                              </span>
                              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} ml-2`}>/quarter</span>
                            </div>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                              ${getMonthlyPrice(validatedProgram.pricePerMonth)}/month
                            </p>
                          </>
                        )}
                        {billingCycle === 'annual' && billingCycles?.annual && (
                          <>
                            <div className="flex items-baseline justify-center">
                              <span className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>$</span>
                              <span className={`text-5xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} ml-1`}>
                                {getMonthlyPrice(validatedProgram.pricePerMonth)}
                              </span>
                              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} ml-2`}>/month</span>
                            </div>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                              ${getAnnualPrice(validatedProgram.pricePerMonth)}/year
                            </p>
                          </>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          navigate('/portal/programdetail', {
                            state: {
                              selectedProgram: validatedProgram,
                              selectedTier: programKey,
                              billingCycle: billingCycle
                            }
                          });
                        }}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors mb-6 mt-auto ${
                          isPlusProgram
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : `bg-gradient-to-r ${getProgramColor(validatedProgram.name)} text-white hover:opacity-90`
                        }`}
                      >
                        Learn More
                      </button>

                      {/* Showcase Features - freeform text rows */}
                      {getShowcaseFeatures(programKey).length > 0 && (
                        <div className="space-y-4 flex-1">
                          {getShowcaseFeatures(programKey).map((feature, idx) => {
                            const item = availableItems.find(availItem => availItem.name === feature.text);
                            const itemIcon = item?.icon || '⭐';
                            const hasFeatureDescription = feature.description?.trim();
                            return (
                              <div key={idx} className={`flex items-center p-2 rounded-md border ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} transition-colors cursor-pointer`}>
                                <button
                                  onClick={() => {
                                    if (hasFeatureDescription) {
                                      showShowcaseFeatureDetails(feature);
                                    } else if (item) {
                                      showItemDetails(item);
                                    } else {
                                      showShowcaseFeatureDetails(feature);
                                    }
                                  }}
                                  className="flex items-center flex-1 text-left hover:text-blue-400 transition-colors"
                                >
                                  <span className="text-lg mr-2">{itemIcon}</span>
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature.text}</span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

              </>
            )}

            {/* Customize Your Own Plan */}
            <div className="mb-12">
              <div className={`w-full flex flex-col justify-center items-center rounded-xl shadow-lg border-2 p-8 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h3 className={`text-2xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {hasStandardPrograms ? <>Don&apos;t see a program that fits your vision?</> : 'Every BBA program is built around your business.'}
                </h3>
                <button
                  onClick={() => navigate('/portal/programdetail', {
                    state: {
                      selectedProgram: CUSTOM_PROGRAM,
                      selectedTier: CUSTOM_TIER,
                      billingCycle
                    }
                  })}
                  className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors shadow-lg border-2 border-red-600 hover:border-red-700"
                >
                  {hasStandardPrograms ? 'Customize Your Own Plan' : 'Build Your Custom Program'}
                  <FaArrowRight className="text-lg" />
                </button>
              </div>
            </div>

            {hasStandardPrograms && (
              <>
            {/* Items Comparison Table */}
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg overflow-hidden border`}>
              <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} text-center`}>Complete Items Comparison</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`px-6 py-4 text-left text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Items</th>
                      {programs && getOrderedProgramKeys().map(programKey => {
                        const validatedProgram = validateProgram(programs[programKey]);
                        return (
                          <th key={programKey} className={`px-6 py-4 text-center text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {validatedProgram.name}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className={darkMode ? 'divide-gray-700' : 'divide-gray-200'}>
                    {(() => {
                      const rows = [];
                      
                      // Add department sections
                      const departments = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
                      departments.forEach(dept => {
                        const deptItems = getOrderedItems(dept);
                        if (deptItems.length > 0) {
                          const isCollapsed = collapsedDepartments[dept];
                          
                          // Add department header row
                          rows.push(
                            <tr key={`dept-${dept}`} className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                              <td colSpan={programs && Object.keys(programs).length > 0 ? Object.keys(programs).length + 1 : 1} className="px-6 py-3">
                                <button
                                  onClick={() => toggleDepartment(dept)}
                                  className={`flex items-center space-x-2 text-sm font-semibold ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'} transition-colors`}
                                >
                                  {isCollapsed ? (
                                    <FaChevronRight className="h-4 w-4" />
                                  ) : (
                                    <FaChevronDown className="h-4 w-4" />
                                  )}
                                  <span>{dept}</span>
                                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>({deptItems.length} items)</span>
                                </button>
                              </td>
                            </tr>
                          );
                          
                          // Add department items (only if not collapsed)
                          if (!isCollapsed) {
                            deptItems.forEach((item) => {
                              rows.push(
                                <tr key={item.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center space-x-3">
                                      <button
                                        onClick={() => showItemDetails(item)}
                                        className={`flex items-center space-x-3 flex-1 text-left ${darkMode ? 'hover:bg-blue-900/20' : 'hover:bg-blue-50'} p-2 rounded-md transition-colors`}
                                      >
                                        <div className="text-2xl">{item.icon || '📦'}</div>
                                        <div className="flex items-center space-x-2">
                                          <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-sm`}>{item.name}</div>
                                          <FaInfoCircle className={`h-4 w-4 ${darkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'} transition-colors`} />
                                        </div>
                                      </button>
                                    </div>
                                  </td>
                                  {programs && getOrderedPrograms().map(([programKey, program]) => {
                                    const validatedProgram = validateProgram(program);
                                    const currentQuantity = validatedProgram.features[item.name]?.quantity || '0';
                                    const isCheckbox = currentQuantity === '0';
                                    const isIncluded = validatedProgram.features[item.name] !== undefined;
                                    
                                    return (
                                      <td key={programKey} className="px-6 py-4 text-center">
                                        {isIncluded ? (
                                          <div className="flex items-center justify-center">
                                            {isCheckbox ? (
                                              <FaCheck className="text-green-500 text-sm" />
                                            ) : (
                                              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {currentQuantity}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-center">
                                            <FaTimes className="text-red-500 text-sm" />
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            });
                          }
                        }
                      });
                      
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Item Details Modal */}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg w-full max-w-md`}>
            <div className="bg-red-600 rounded-t-lg p-4">
              <h3 className="text-lg font-semibold text-white text-center">Item Details</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="text-4xl">{selectedItem.icon || '📦'}</div>
                <div>
                  <h4 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedItem.name}</h4>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getDepartmentColor(selectedItem.department)}`}>
                    {selectedItem.department}
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <h5 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Description</h5>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{selectedItem.description}</p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={closeItemModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Showcase Feature Details Modal */}
      {selectedShowcaseFeature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg w-full max-w-md`}>
            <div className="bg-yellow-500 rounded-t-lg p-4 relative">
              <h3 className="text-lg font-semibold text-white text-center pr-8">{selectedShowcaseFeature.text}</h3>
              <button
                onClick={closeShowcaseFeatureModal}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-200 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              {selectedShowcaseFeature.description?.trim() ? (
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>
                  {selectedShowcaseFeature.description}
                </p>
              ) : (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No description available.</p>
              )}
              <div className="flex justify-end mt-4">
                <button
                  onClick={closeShowcaseFeatureModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalPrograms;

